import React, { useState } from 'react';
import { Prospect, Proposal } from '../../shared/types';
import { calculateROI } from '../../core/roi/calculator';
import { Card } from '../../components/ui/Card';
import { AlertCircle, DollarSign, Users, Target } from 'lucide-react';

interface ROICalculatorSectionProps {
  prospect: Prospect;
  proposal: Proposal;
  custoServicosMes?: number;
}

export const ROICalculatorSection: React.FC<ROICalculatorSectionProps> = ({
  prospect,
  proposal,
  custoServicosMes = 2000,
}) => {
  const defaultHypotheses = proposal.roi_hypotheses || {
    ticket_medio: 500,
    margem_estimada_pct: 35,
    clientes_atuais_mes: 15,
    leads_atuais_mes: 40,
    taxa_conversao_pct: 10,
    investimento_midia_mes: 800,
  };

  const [ticketMedio, setTicketMedio] = useState<number>(defaultHypotheses.ticket_medio);
  const [margemPct, setMargemPct] = useState<number>(defaultHypotheses.margem_estimada_pct);
  const [investimentoMidia, setInvestimentoMidia] = useState<number>(
    defaultHypotheses.investimento_midia_mes
  );

  const roiData = calculateROI(
    {
      ...defaultHypotheses,
      ticket_medio: ticketMedio,
      margem_estimada_pct: margemPct,
      investimento_midia_mes: investimentoMidia,
    },
    custoServicosMes
  );

  return (
    <section className="py-16 border-b border-slate-100 bg-white">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <span className="text-xs font-bold uppercase tracking-wider text-[var(--ym-prospect-primary)]">Simulador Transparente</span>
          <h2 className="text-2xl md:text-4xl font-extrabold text-slate-900 mt-2">
            Estimativa de Retorno sobre Investimento (ROI)
          </h2>
          <p className="text-slate-600 mt-3 text-sm md:text-base">
            Altere as hipóteses da {prospect.trade_name} abaixo para visualizar a projeção de retorno financeiro e o número de novas vendas necessárias para cobrir o investimento.
          </p>
        </div>

        {/* Inputs Interativos */}
        <div className="grid md:grid-cols-3 gap-6 mb-10 bg-slate-50 p-6 rounded-2xl border border-slate-200">
          <div>
            <label className="block text-xs font-bold uppercase text-slate-500 mb-2 flex items-center gap-1.5">
              <DollarSign className="w-4 h-4 text-emerald-500" />
              Ticket Médio por Cliente (R$)
            </label>
            <input
              type="number"
              value={ticketMedio}
              onChange={(e) => setTicketMedio(Number(e.target.value) || 0)}
              className="w-full bg-white border border-slate-300 rounded-xl px-4 py-2.5 text-slate-900 font-semibold focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-shadow"
            />
          </div>

          <div>
            <label className="block text-xs font-bold uppercase text-slate-500 mb-2 flex items-center gap-1.5">
              <Target className="w-4 h-4 text-indigo-500" />
              Margem de Lucro Estimada (%)
            </label>
            <input
              type="number"
              value={margemPct}
              onChange={(e) => setMargemPct(Number(e.target.value) || 0)}
              className="w-full bg-white border border-slate-300 rounded-xl px-4 py-2.5 text-slate-900 font-semibold focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-shadow"
            />
          </div>

          <div>
            <label className="block text-xs font-bold uppercase text-slate-500 mb-2 flex items-center gap-1.5">
              <Users className="w-4 h-4 text-purple-500" />
              Verba Mensal de Anúncios (R$)
            </label>
            <input
              type="number"
              value={investimentoMidia}
              onChange={(e) => setInvestimentoMidia(Number(e.target.value) || 0)}
              className="w-full bg-white border border-slate-300 rounded-xl px-4 py-2.5 text-slate-900 font-semibold focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-shadow"
            />
          </div>
        </div>

        {/* Resumo Payback */}
        <div className="mb-10 p-5 rounded-2xl bg-indigo-50 border border-indigo-100 text-center">
          <p className="text-sm text-indigo-900">
            Com um investimento mensal total estimado em{' '}
            <strong className="text-indigo-950 text-base">
              R$ {roiData.custo_total_investimento.toLocaleString('pt-BR')}
            </strong>{' '}
            (serviços + mídia), a {prospect.trade_name} precisa de apenas{' '}
            <span className="text-emerald-700 font-extrabold text-lg px-2 py-0.5 rounded bg-emerald-100 border border-emerald-200">
              {roiData.esperado.vendas_necessarias_payback} novas vendas/mês
            </span>{' '}
            para empatar o investimento (ponto de equilíbrio / payback).
          </p>
        </div>

        {/* Cenários */}
        <div className="grid md:grid-cols-3 gap-6">
          {/* Conservador */}
          <Card className="border-slate-200 bg-white shadow-sm">
            <div className="text-center pb-4 border-b border-slate-100 mb-4">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Cenário</span>
              <h3 className="text-xl font-extrabold text-slate-400 mt-1">Conservador</h3>
            </div>
            <div className="space-y-4 text-center">
              <div>
                <span className="text-xs text-slate-400">Receita Adicional Estimada</span>
                <p className="text-2xl font-extrabold text-slate-300 mt-0.5">
                  R$ {roiData.conservador.receita_adicional_estimada.toLocaleString('pt-BR')}
                </p>
              </div>
              <div>
                <span className="text-xs text-slate-400">Lucro Adicional Estimado</span>
                <p className="text-lg font-bold text-emerald-500 mt-0.5">
                  R$ {roiData.conservador.lucro_adicional_estimado.toLocaleString('pt-BR')}
                </p>
              </div>
              <div className="pt-2 border-t border-slate-100">
                <span className="text-xs text-slate-400">ROI Estimado</span>
                <p className="text-xl font-extrabold text-indigo-400 mt-0.5">
                  +{roiData.conservador.roi_estimado_pct}%
                </p>
              </div>
            </div>
          </Card>

          {/* Esperado (Destaque) */}
          <Card className="border-indigo-200 bg-indigo-50/30 relative shadow-md">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-indigo-600 text-white text-[10px] font-extrabold uppercase px-3 py-1 rounded-full tracking-wider shadow-lg">
              Meta Recomendada
            </div>
            <div className="text-center pb-4 border-b border-slate-200 mb-4 pt-2">
              <span className="text-xs font-bold text-indigo-600 uppercase tracking-wider">Cenário</span>
              <h3 className="text-xl font-extrabold text-slate-900 mt-1">Esperado</h3>
            </div>
            <div className="space-y-4 text-center">
              <div>
                <span className="text-xs text-indigo-600/80">Receita Adicional Estimada</span>
                <p className="text-3xl font-extrabold text-[var(--ym-prospect-primary)] mt-0.5">
                  R$ {roiData.esperado.receita_adicional_estimada.toLocaleString('pt-BR')}
                </p>
              </div>
              <div>
                <span className="text-xs text-indigo-600/80">Lucro Adicional Estimado</span>
                <p className="text-xl font-bold text-emerald-600 mt-0.5">
                  R$ {roiData.esperado.lucro_adicional_estimado.toLocaleString('pt-BR')}
                </p>
              </div>
              <div className="pt-2 border-t border-slate-200">
                <span className="text-xs text-indigo-600/80">ROI Estimado</span>
                <p className="text-2xl font-extrabold text-emerald-600 mt-0.5">
                  +{roiData.esperado.roi_estimado_pct}%
                </p>
              </div>
            </div>
          </Card>

          {/* Otimista */}
          <Card className="border-emerald-200 bg-white shadow-sm">
            <div className="text-center pb-4 border-b border-slate-100 mb-4">
              <span className="text-xs font-bold text-emerald-500 uppercase tracking-wider">Cenário</span>
              <h3 className="text-xl font-extrabold text-slate-400 mt-1">Otimista</h3>
            </div>
            <div className="space-y-4 text-center">
              <div>
                <span className="text-xs text-slate-400">Receita Adicional Estimada</span>
                <p className="text-2xl font-extrabold text-slate-300 mt-0.5">
                  R$ {roiData.otimista.receita_adicional_estimada.toLocaleString('pt-BR')}
                </p>
              </div>
              <div>
                <span className="text-xs text-slate-400">Lucro Adicional Estimado</span>
                <p className="text-lg font-bold text-emerald-500 mt-0.5">
                  R$ {roiData.otimista.lucro_adicional_estimado.toLocaleString('pt-BR')}
                </p>
              </div>
              <div className="pt-2 border-t border-slate-100">
                <span className="text-xs text-slate-400">ROI Estimado</span>
                <p className="text-xl font-extrabold text-emerald-400 mt-0.5">
                  +{roiData.otimista.roi_estimado_pct}%
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Disclaimer Legal Obrigatório (Regra Pétrea #24) */}
        <div className="mt-8 p-4 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-3 shadow-sm">
          <AlertCircle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
          <p className="text-xs text-slate-600 leading-relaxed">
            <strong className="text-slate-900">Aviso Importante:</strong> {roiData.disclaimer}
          </p>
        </div>
      </div>
    </section>
  );
};
